import { Component, Input, Output, EventEmitter, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChatUser } from '../../models';

@Component({
  selector: 'app-user-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './user-picker.html',
  styleUrls: ['./user-picker.scss'],
})
export class UserPickerComponent {
  @Input() users: ChatUser[] = [];
  @Input() currentUserId = '';
  @Output() participantsConfirmed = new EventEmitter<{ userIds: string[]; topic: string }>();
  @Output() cancelled = new EventEmitter<void>();

  searchText = signal('');
  selectedIds = signal<Set<string>>(new Set());

  /** Users excluding the current user, filtered by search text. */
  filteredUsers = computed(() => {
    const q = this.searchText().toLowerCase().trim();
    return this.users
      .filter((u) => u.id !== this.currentUserId)
      .filter((u) => !q || u.displayName.toLowerCase().includes(q));
  });

  isSelected(userId: string): boolean {
    return this.selectedIds().has(userId);
  }

  toggleUser(userId: string): void {
    this.selectedIds.update((s) => {
      const next = new Set(s);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }

  getUserName(id: string): string {
    return this.users.find((u) => u.id === id)?.displayName ?? id;
  }

  confirm(): void {
    const ids = Array.from(this.selectedIds());
    if (ids.length === 0) return;

    const names = ids
      .map((id) => this.users.find((u) => u.id === id)?.displayName ?? id)
      .join(', ');
    const topic = ids.length === 1 ? names : `Group: ${names}`;

    this.participantsConfirmed.emit({ userIds: ids, topic });
  }

  onSearch(value: string): void {
    this.searchText.set(value);
  }

  /** Initials fallback when no avatar URL. */
  initials(name: string): string {
    return name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
}
